<template>
  <span>
    <!-- ▼メッセージ作成-->
    <entry-message-register-form
      v-show="!isSendable"
      ref="entryMessageRegisterForm"
      v-model="message"
      :entry-ids="entryIds"
      @filledOut="onFilledOut"
    />
    <!-- ▲メッセージ作成-->
    <!-- ▼メッセージ確認-->
    <entry-message-register-confirm
      v-show="isSendable"
      :message="confirmMessage"
      @back="isSendable = false"
      @submitted="onRegistered"
      @reserved="onReserved"
    />
    <!-- ▲メッセージ確認-->
  </span>
</template>

<script>
import EntryMessageRegisterForm from '@/components/molecules/EntryMessageRegisterForm'
import EntryMessageRegisterConfirm from '@/components/molecules/EntryMessageRegisterConfirm'
import _ from 'lodash'
import Indicator from '@/lib/indicator'

function createMessage (entryIds, messageToApplicant) {
  return {
    entryIds: entryIds,
    reservedId: messageToApplicant?.entryMailReservationReservedId,
    subject: messageToApplicant?.message.subject ?? '',
    body: messageToApplicant?.message.body ?? '',
    files: messageToApplicant?.files ?? [],
    schedule: {
      candidateDates: messageToApplicant?.interviewAdjustment?.candidateDates ?? [],
      place: messageToApplicant?.interviewAdjustment?.place?.id ?? null,
      duration: messageToApplicant?.interviewAdjustment?.duration,
      contentIds: messageToApplicant?.interviewAdjustment?.contentList.map(content => content.id) ?? [],
      otherContents: messageToApplicant?.interviewAdjustment?.contentOther ?? '',
      remarks: messageToApplicant?.interviewAdjustment?.remarks ?? ''
    },
    isPhaseChange: messageToApplicant?.selection?.isPhaseChange ?? null,
    phaseId: messageToApplicant?.selection?.phase?.id,
    isStatusChange: messageToApplicant?.selection?.isStatusChange ?? null,
    statusId: messageToApplicant?.selection?.status?.id
  }
}

export default {
  components: {
    EntryMessageRegisterForm,
    EntryMessageRegisterConfirm
  },
  props: {
    entryIds: {
      type: Array,
      required: true
    },
    messageToApplicant: {
      type: Object,
      default: () => { return null }
    }
  },
  data () {
    return {
      message: createMessage(this.entryIds, this.messageToApplicant),
      isCandidateDate: false,
      isSendable: false,
      totalEntryCountSum: 0,
      executeEntryCountSum: 0
    }
  },
  computed: {
    confirmMessage () {
      return this.isCandidateDate ? this.message : _.set(_.cloneDeep(this.message), 'schedule', null)
    },
    fileIds () {
      return this.message.files.map(file => file.id)
    },
    hasSameFileName () {
      return this.message.files.length === _.uniqBy(this.message.files, 'name').length
    },
    isNoExecute () {
      return this.executeEntryCountSum === 0
    },
    isAllExecute () {
      return this.executeEntryCountSum === this.totalEntryCountSum
    },
    executeResultLabel () {
      return `${this.totalEntryCountSum}名中${this.executeEntryCountSum}名`
    },
    sendExecuteTargetLabel () {
      if (this.message.isPhaseChange && this.message.isStatusChange) return '送信・選考フェーズ、ステータス更新'
      if (this.message.isPhaseChange) return '送信・選考フェーズ更新'
      if (this.message.isStatusChange) return '送信・選考ステータス更新'
      return '送信'
    }
  },
  watch: {
    entryIds: {
      handler () {
        this.fetchCurrentSelection()
      }
    },
    async messageToApplicant (val) {
      this.$set(this, 'message', createMessage(this.entryIds, val))
      await this.fetchCurrentSelection()

      // フェーズ・ステータスに指定がある場合は上書き
      if (val?.selection?.isPhaseChange) this.$set(this.message, 'phaseId', val?.selection?.phase?.id)
      if (val?.selection?.isStatusChange) this.$set(this.message, 'statusId', val?.selection?.status?.id)
    }
  },
  created () {
    this.entryIds.length && this.fetchCurrentSelection()
  },
  methods: {
    onFilledOut (isCandidateDate) {
      this.isCandidateDate = isCandidateDate
      this.isSendable = true
    },
    onRegistered () {
      !this.hasSameFileName ? this.showDialogSend() : this.chunkSend()
    },
    showDialogSend () {
      this.$dialog.confirm('同じファイル名のものが複数添付されています。このまま送信してよいですか？')
        // はいを押下した時
        .then(() => {
          this.chunkSend()
        })
        // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {})
    },
    chunkSend () {
      const chunkEntryIds = _.chunk(this.confirmMessage.entryIds, 50)
      this.send(chunkEntryIds)
    },
    send (chunkEntryIds) {
      const entryIds = chunkEntryIds.shift()
      // リクエストの形に整形
      const requestParam = this.requestMessageParam(entryIds)
      Indicator.emitShow()

      // 通常の送信か、予約編集からの即時送信かでAPIの呼び先を変える
      // - 予約IDを持つ場合は応募IDは必ず1件
      const reservedId = this.confirmMessage.reservedId
      const apiPath = reservedId
        ? `/entry/message/send/entries/${this.entryIds[0]}/message/send`
        : '/entry/message/send'

      this.$http.secured.post(apiPath, requestParam).then((response) => {
        this.$set(this, 'totalEntryCountSum', this.totalEntryCountSum + response.data.totalEntryCount)
        this.$set(this, 'executeEntryCountSum', this.executeEntryCountSum + response.data.sendEntryCount)

        if (_.isEmpty(chunkEntryIds)) {
          if (this.isNoExecute) {
            this.$toasted.global.error({ message: '退会・予約中の応募者のため、送信できません。' })
            this.resetCountSum()
          } else if (this.isAllExecute) {
            this.$toasted.global.success({ message: `${this.executeResultLabel}の${this.sendExecuteTargetLabel}が完了しました。` })
            this.successTransition()
            this.reset()
          } else {
            this.$toasted.global.success({ message: `${this.executeResultLabel}の${this.sendExecuteTargetLabel}が完了しました。<br>※退会・予約中の応募者には送信できません。` })
            this.successTransition()
            this.reset()
          }
          Indicator.emitHide()
        } else {
          this.send(chunkEntryIds)
        }
      }).catch(() => {
        Indicator.emitHide()
      })
    },
    onReserved () {
      !this.hasSameFileName ? this.showDialogReserve() : this.chunkReserve()
    },
    showDialogReserve () {
      this.$dialog.confirm('同じファイル名のものが複数添付されています。このまま送信予約してよいですか？')
        // はいを押下した時
        .then(() => {
          this.chunkReserve()
        })
        // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {})
    },
    chunkReserve () {
      const chunkEntryIds = _.chunk(this.confirmMessage.entryIds, 50)
      this.reserve(chunkEntryIds)
    },
    reserve (chunkEntryIds) {
      const entryIds = chunkEntryIds.shift()
      // リクエストの形に整形
      const requestParam = this.requestMessageParam(entryIds)

      // 予約作成か、予約編集かでAPIの呼び先を変える
      // - 予約IDを持つ場合は応募IDは必ず1件
      const reservedId = this.confirmMessage.reservedId
      const apiPath = reservedId
        ? `/entry/message/send/entries/${this.entryIds[0]}/message/reserved_reservation`
        : '/entry/message/send/entries/message/reserved_reservation/bulk_register'

      Indicator.emitShow()
      this.$http.secured.post(apiPath, requestParam).then((response) => {
        this.$set(this, 'totalEntryCountSum', this.totalEntryCountSum + response.data.totalEntryCount)
        this.$set(this, 'executeEntryCountSum', this.executeEntryCountSum + response.data.reserveEntryCount)

        if (_.isEmpty(chunkEntryIds)) {
          if (this.isNoExecute) {
            this.$toasted.global.error({ message: '退会・予約中の応募者のため、設定できません。' })
            this.resetCountSum()
          } else if (this.isAllExecute) {
            this.$toasted.global.success({ message: `${this.executeResultLabel}の予約が完了しました。` })
            this.reset()
            this.successTransition()
          } else {
            this.$toasted.global.success({ message: `${this.executeResultLabel}の予約が完了しました。<br>※退会・予約中の応募者には設定できません。` })
            this.reset()
            this.successTransition()
          }
          Indicator.emitHide()
        } else {
          this.reserve(chunkEntryIds)
        }
      }).catch(() => {
        Indicator.emitHide()
      })
    },
    async fetchCurrentSelection () {
      const entryId = this.entryIds[0]
      await this.$http.secured.get(`/entry/view/entries/${entryId}/current_selection`).then(response => {
        this.$set(this.message, 'phaseId', response.data.phaseId)
        this.$set(this.message, 'statusId', response.data.statusId)
      })
    },
    reset () {
      this.$set(this, 'message', createMessage(this.entryIds, null))
      this.resetCountSum()
      this.fetchCurrentSelection()
      this.$refs.entryMessageRegisterForm.reset()
      this.$scrollTo('#message-register', 0, { offset: -35 })
    },
    resetCountSum () {
      this.$set(this, 'totalEntryCountSum', 0)
      this.$set(this, 'executeEntryCountSum', 0)
    },
    requestMessageParam (entryIds) {
      // 添付ファイル情報を除き、添付ファイルIDの配列を付与
      const requestParam = _.omit(_.cloneDeep(this.confirmMessage), ['files'])
      _.set(requestParam, 'fileIds', this.fileIds)

      // 応募ID一覧を追加
      requestParam.entryIds = entryIds || []

      // 選考フェーズ・ステータスを変更しない場合はパラメータを削除
      if (!requestParam.isPhaseChange) delete requestParam.phaseId
      if (!requestParam.isStatusChange) delete requestParam.statusId

      return requestParam
    },
    successTransition () {
      if (this.$route.params.entryId) {
        this.isSendable = false
        this.$emit('registered')
      } else {
        // 一括の場合
        this.$router.push('/entries')
      }
    }
  }
}
</script>
